<script lang="ts" setup>
import {useLang} from '@/ts/composables/stateful/use-lang';
import {useLanguageChange} from '@/ts/composables/stateful/use-language-change';
import type {Language} from '@/ts/types/component/localization.type';
import {MULTI_LANGUAGE} from '@/ts/utils/consts';
import {QBtnDropdown, QItem, QItemSection} from 'quasar';
import SvgIllustration from '@/vue/atoms/svg-illustration.vue';
import {useApi} from '@/ts/composables/stateful/use-api';
import {persistentStorage} from '@/ts/instances/persistant-storage';
import {useLogger} from '@/ts/composables/pure/use-logger';
import {ref} from 'vue';

const isDropdownOpen = ref(false);
const {lang} = useLang();
const {languageOptions, currentLanguage, applyLanguageChange} = useLanguageChange();
const api = useApi();
const logger = useLogger();

async function switchToLanguage(newLanguage: Language): Promise<void> {
  if (newLanguage === lang.value) {
    return;
  }

  if (persistentStorage.auth) {
    try {
      await api.me.updateMe({
        languageCode: newLanguage,
      });
    } catch (error) {
      logger.error('Failed to update user language in backend:', error)();
      // Continue with local language change even if backend update fails
    }
  }

  await applyLanguageChange(newLanguage);
}
</script>

<template>
  <q-btn-dropdown
    v-show="MULTI_LANGUAGE"
    v-model="isDropdownOpen"
    class="language-selector"
    content-class="balm-dropdown-menu language-selector-menu"
    menu-anchor="bottom end"
    menu-self="top end"
    :menu-offset="[0, 8]"
    flat
    dense
  >
    <template #label>
      <div class="selected-language">
        <span class="label">{{ currentLanguage.label }}</span>
        <svg-illustration name="chevron-down.svg" class="chevron" :class="{'chevron--open': isDropdownOpen}"/>
      </div>
    </template>

    <!-- Quasar 2.28 dropped the default role on QMenu, so the list declares it -->
    <q-list role="menu">
      <q-item
        v-for="option in languageOptions"
        :key="option.value"
        v-close-popup
        clickable
        role="menuitem"
        class="language-option"
        :class="{'selected-option': option.value === lang}"
        :aria-current="option.value === lang ? 'true' : undefined"
        @click="switchToLanguage(option.value)"
      >
        <q-item-section>
          <div class="option-language">
            <span>{{ option.label }}</span>
          </div>
        </q-item-section>
        <q-item-section v-if="option.value === lang" side>
          <svg-illustration name="check.svg" class="option-check"/>
        </q-item-section>
      </q-item>
    </q-list>
  </q-btn-dropdown>
</template>

<style lang="scss" scoped>
// Fixed paddings on the compact controls: the responsive spacing tokens double
// at desktop, blowing these ornaments up to twice the size the design specs.
.language-selector {
  @include focus-visible;
  padding: 4px 12px;
  border-radius: var(--button-border-radius);

  // Quasar's default arrow is a Material filled triangle; the design uses an
  // outline chevron (rendered in the label slot so it can rotate with state).
  :deep(.q-btn-dropdown__arrow) {
    display: none;
  }
}

.selected-language {
  display: flex;
  align-items: center;
  gap: var(--Spacing-spacing-2);
}

.label {
  @include font-body-sm-bold;
}

.chevron {
  @include dropdown-chevron;
}

.language-option {
  min-height: 32px;
  padding: 8px 12px;
  background: var(--Neutral-UI-neutral-ui-10);
}

.option-language {
  @include font-body-sm;
  display: flex;
  align-items: center;
}

// Per the prototype the active language is marked with a bold label, a
// trailing check, and the popover (ui-0) row background.
.selected-option {
  background: var(--Neutral-UI-neutral-ui-0);

  .option-language {
    font-weight: 700;
  }
}

.option-check {
  @include dropdown-option-check;
}
</style>

<style lang="scss">
// Body-level portal, so this cannot be scoped; the class is set via
// content-class. !important: Quasar's `fit` writes an inline min-width (the
// trigger's width) AFTER computing the right-aligned position from this one,
// which would shrink the menu and break the alignment.
.q-menu.language-selector-menu {
  min-width: 160px !important;
}
</style>
